import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, CheckCircle2, AlertCircle, Clock, FileText, ShieldAlert,
  Pause, Play, MessageSquare, RefreshCw, X, Download, Upload, Paperclip,
  Image as ImageIcon, Trash2, MoreVertical, Star, Receipt, Upload as UploadIcon,
  ThumbsUp, ThumbsDown, Share2, Copy, Check,
} from 'lucide-react';
import {
  useContracts, selectFundCoverage, isMilestoneAssetCovered, computeAcceptancePeriodDays,
} from './ContractsContext';
import type {
  ContractBundle, Contract, Milestone, PaymentPlanEntry, ReleasePlanEntry,
  PaymentEntryStatus, MilestoneStatus, DeliveryAsset, ContractInvoice,
} from './types';
import { contractApiService } from '../../services/contractApiService';
import { mediaUploadService } from '../../services/mediaUploadService';
import { translateBackendError } from '../../utils/translateBackendError';
import { buildContractShareUrl } from '../../utils/share';
import { useToast } from '../../components/UI/Toast';

// Map a delivery-asset type to its human label (matches the wizard).
const ASSET_TYPE_LABELS: Record<DeliveryAsset['type'], string> = {
  source_file: '源文件',
  model_file: '模型文件',
  engineering_file: '工程文件',
  commercial_license: '商用授权',
  no_watermark: '无水印成品',
  prompt: '提示词',
  other: '其他',
};

const isImageUrl = (url?: string) =>
  !!url && url !== '#' && /\.(png|jpe?g|gif|webp|bmp|svg)(\?|$)/i.test(url);
const isVideoUrl = (url?: string) =>
  !!url && url !== '#' && /\.(mp4|mov|webm|m4v)(\?|$)/i.test(url);
const hasFile = (url?: string) => !!url && url !== '#';

const formatRmb = (n: number) => `¥${n.toLocaleString('zh-CN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
const formatDate = (ms?: number) => ms ? new Date(ms).toLocaleDateString('zh-CN') : '—';
const formatDateTime = (ms?: number) => ms ? new Date(ms).toLocaleString('zh-CN') : '—';

// =====================================================================
// Page
// =====================================================================

// Full-page shell matching the contracts list layout so the detail
// page sits below the global header with consistent padding/width.
const DetailShell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="min-h-screen bg-[#FDFDFD] pt-24 pb-20">
    <div className="max-w-5xl mx-auto px-6 md:px-12">{children}</div>
  </div>
);

// Shown when a non-party opens a project via a share link: prompts for
// the share password, redeems it (granting collaborator access), then
// reloads the project.
const RedeemShareGate: React.FC<{ shareToken: string; onRedeemed: () => void }> = ({ shareToken, onRedeemed }) => {
  const nav = useNavigate();
  const { showToast } = useToast();
  const [meta, setMeta] = useState<{ title: string; alreadyMember: boolean } | null>(null);
  const [invalid, setInvalid] = useState(false);
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  React.useEffect(() => {
    let active = true;
    void contractApiService.getShareMeta(shareToken).then(res => {
      if (!active) return;
      if (res.success) setMeta({ title: res.title, alreadyMember: res.alreadyMember });
      else setInvalid(true);
    });
    return () => { active = false; };
  }, [shareToken]);

  const redeem = async () => {
    setBusy(true);
    const res = await contractApiService.redeemShare(shareToken, password);
    setBusy(false);
    if (!res.success) { showToast(translateBackendError(res.error, '密码错误或链接已失效'), 'error'); return; }
    showToast('已加入项目', 'success');
    onRedeemed();
  };

  if (invalid) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
        <p className="text-sm text-slate-500">分享链接无效或已失效</p>
        <button onClick={() => nav('/contracts')} className="mt-4 text-sm text-blue-600">返回列表</button>
      </div>
    );
  }
  return (
    <div className="max-w-md mx-auto bg-white rounded-2xl border border-slate-200 p-8">
      <h2 className="text-lg font-semibold text-slate-900 mb-1">加入协作项目</h2>
      <p className="text-sm text-slate-500 mb-5">
        {meta ? `你被邀请协作管理「${meta.title}」。请输入访问密码加入。` : '正在加载分享信息...'}
      </p>
      <label className="block text-xs text-slate-500 mb-1">访问密码</label>
      <input
        type="password"
        value={password}
        onChange={e => setPassword(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter' && password) redeem(); }}
        placeholder="请输入分享者提供的密码"
        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-blue-100"
      />
      <div className="flex justify-end gap-2">
        <button onClick={() => nav('/contracts')} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 rounded-lg">取消</button>
        <button
          onClick={redeem}
          disabled={busy || !password}
          className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          {busy ? '加入中...' : '加入项目'}
        </button>
      </div>
    </div>
  );
};

const ContractDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const shareToken = searchParams.get('share') || undefined;
  const { bundleById, isViewerClient, isViewerCreator, loadDetail, loading } = useContracts();
  const bundle = id ? bundleById(id) : undefined;
  // Tracks whether our direct fetch resolved, so we can tell "still
  // loading" apart from "loaded but inaccessible" (→ maybe redeem a share).
  const [fetchDone, setFetchDone] = React.useState(false);

  // Direct URL hits won't have the bundle in cache yet — fetch it.
  React.useEffect(() => {
    if (id && !bundle && !loading) {
      setFetchDone(false);
      void loadDetail(id).finally(() => setFetchDone(true));
    } else if (bundle) {
      setFetchDone(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!bundle && (loading || !fetchDone)) {
    return (
      <DetailShell>
        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center text-sm text-slate-400">
          加载中...
        </div>
      </DetailShell>
    );
  }
  // Inaccessible (not a party) but opened via a share link → let the
  // viewer redeem it with the password to gain collaborator access.
  if (!bundle && shareToken) {
    return (
      <DetailShell>
        <RedeemShareGate
          shareToken={shareToken}
          onRedeemed={() => { setFetchDone(false); void loadDetail(id!).finally(() => setFetchDone(true)); }}
        />
      </DetailShell>
    );
  }
  if (!bundle) {
    return (
      <DetailShell>
        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
          <p className="text-sm text-slate-500">项目不存在或无权访问</p>
          <button onClick={() => nav('/contracts')} className="mt-4 text-sm text-blue-600">
            返回列表
          </button>
        </div>
      </DetailShell>
    );
  }

  const cov = selectFundCoverage(bundle);
  const isClient = isViewerClient(bundle);
  const isCreator = isViewerCreator(bundle);

  return (
    <DetailShell>
      <button
        type="button"
        onClick={() => {
          // Go back to the exact list view (role + tab preserved in its
          // URL) when we came from there; fall back to the list root on a
          // direct/deep link with no in-app history.
          if (typeof window !== 'undefined' && (window.history.state?.idx ?? 0) > 0) nav(-1);
          else nav('/contracts');
        }}
        className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 mb-4"
      >
        <ArrowLeft className="w-4 h-4" /> 返回
      </button>

      {/* Header card */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6 mb-6">
        <div className="flex items-start justify-between gap-6 mb-5">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-2">
              <span className={`text-[10px] font-medium uppercase tracking-wider px-1.5 py-0.5 rounded ${
                bundle.contract.projectType === 'large' ? 'bg-purple-50 text-purple-700' : 'bg-sky-50 text-sky-700'
              }`}>
                {bundle.contract.projectType === 'large' ? '大项目' : '小项目'}
              </span>
              <StateBadge state={bundle.contract.acceptanceState} />
            </div>
            <h1 className="text-xl font-semibold text-slate-900">{bundle.contract.title}</h1>
            <p className="text-sm text-slate-500 mt-1.5 leading-relaxed">{bundle.contract.description}</p>
          </div>
          <div className="flex items-start gap-3 shrink-0">
            {/* The 大项目·进行中 metrics grid below repeats 总金额 as
                项目总金额, so only show the headline amount when that
                grid isn't rendered. */}
            {!(bundle.contract.projectType === 'large' && bundle.contract.acceptanceState === 'active') && (
              <div className="text-right">
                <div className="text-2xl font-semibold text-slate-900">{formatRmb(bundle.contract.totalAmount)}</div>
                <div className="text-xs text-slate-400 mt-0.5">总金额</div>
              </div>
            )}
            <ContractOverflowMenu bundle={bundle} />
          </div>
        </div>

        <div className="flex items-center gap-6 text-sm border-t border-slate-100 pt-4">
          <PartyChip role="客户" nickname={bundle.contract.client.nickname} highlighted={isClient} />
          <PartyChip role="创作者" nickname={bundle.contract.creator.nickname} highlighted={isCreator} />
          <div className="ml-auto text-xs text-slate-400">
            创建于 {formatDate(bundle.contract.createdAt)}
            {bundle.contract.signedAt && <> · 签署于 {formatDate(bundle.contract.signedAt)}</>}
          </div>
        </div>

        <LifecycleActions bundle={bundle} />

        {/* Fund coverage banner — only relevant for 大项目 */}
        {bundle.contract.projectType === 'large' && bundle.contract.acceptanceState === 'active' && (
          <div className="mt-5 grid grid-cols-6 gap-3 pt-4 border-t border-slate-100">
            <Metric label="项目总金额" value={formatRmb(bundle.contract.totalAmount)} />
            <Metric label="已付款" value={formatRmb(cov.totalPaid)} />
            <Metric label="项目资金余额" value={formatRmb(cov.availableBalance)} positive={cov.availableBalance > 0} />
            <Metric label="已放款" value={formatRmb(cov.totalReleased)} />
            <Metric label="待放款" value={formatRmb(bundle.contract.totalAmount - cov.totalReleased)} />
            <Metric label="尾款" value={formatRmb(bundle.contract.tailAmount)} hint={`${bundle.contract.tailPercent}%`} />
          </div>
        )}
      </div>

      {/* Completed contracts get the mutual-review + invoice section. */}
      {bundle.contract.acceptanceState === 'completed' && (
        <CompletionSection bundle={bundle} />
      )}

      {/* 大项目 timeline + payment/release plans */}
      {bundle.contract.projectType === 'large' ? (
        <LargeProjectView bundle={bundle} />
      ) : (
        <SmallProjectView bundle={bundle} />
      )}
    </DetailShell>
  );
};

// =====================================================================
// Large project main view — milestone timeline + plans sidebar
// =====================================================================

// Create a password-protected share link so another logged-in user can
// join the project as a collaborator. Owner sets a password, then copies
// the link + password to send (Baidu-netdisk style).
const ShareProjectModal: React.FC<{ contract: Contract; onClose: () => void }> = ({ contract, onClose }) => {
  const { showToast } = useToast();
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const create = async () => {
    if (password.trim().length < 4) { showToast('请设置至少 4 位的访问密码', 'error'); return; }
    setBusy(true);
    const res = await contractApiService.createShare(contract.id, password.trim());
    setBusy(false);
    if (!res.success) { showToast(translateBackendError(res.error, '创建分享链接失败'), 'error'); return; }
    setLink(buildContractShareUrl(contract.id, res.shareToken));
  };

  const copyAll = async () => {
    if (!link) return;
    const text = `我在 CraftCloud 云作 邀请你协作管理项目「${contract.title}」\n链接：${link}\n访问密码：${password.trim()}`;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      showToast('复制失败，请手动复制', 'error');
    }
  };

  return (
    <Modal title="分享项目协作" onClose={onClose}>
      {!link ? (
        <>
          <p className="text-sm text-slate-500 mb-4">
            设置一个访问密码，生成分享链接。对方登录后输入密码即可加入项目，一起审核里程碑、提修改意见等。
          </p>
          <label className="block text-xs text-slate-500 mb-1">访问密码 <span className="text-rose-500">*</span></label>
          <input
            type="text"
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder="至少 4 位，将随链接一起发送给对方"
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-blue-100"
          />
          <div className="flex justify-end gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 rounded-lg">取消</button>
            <button
              onClick={create}
              disabled={busy || password.trim().length < 4}
              className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {busy ? '生成中...' : '生成分享链接'}
            </button>
          </div>
        </>
      ) : (
        <>
          <p className="text-sm text-slate-500 mb-4">分享链接已生成，把下面的链接和密码一起发给对方即可。</p>
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 mb-3 space-y-2 text-sm">
            <div className="break-all"><span className="text-slate-400">链接：</span>{link}</div>
            <div><span className="text-slate-400">访问密码：</span><span className="font-semibold select-all">{password.trim()}</span></div>
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 rounded-lg">完成</button>
            <button
              onClick={copyAll}
              className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 inline-flex items-center gap-1.5"
            >
              {copied ? <><Check className="w-4 h-4" /> 已复制</> : <><Copy className="w-4 h-4" /> 复制链接和密码</>}
            </button>
          </div>
        </>
      )}
    </Modal>
  );
};

// Three-dots overflow menu for destructive / less-common actions
// (取消合同, 发起申诉). Self-contained — owns its own menu + confirm
// modal state — so it can be dropped into the header next to 总金额.
// Renders nothing when neither action is available to this viewer.
export const ContractOverflowMenu: React.FC<{ bundle: ContractBundle }> = ({ bundle }) => {
  const { dispatch, viewerId } = useContracts();
  const { showToast } = useToast();
  const [busy, setBusy] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [disputeOpen, setDisputeOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  const contract = bundle.contract;
  const isInitiator =
    (contract.initiatorRole === 'client' && contract.client.userId === viewerId) ||
    (contract.initiatorRole === 'creator' && contract.creator.userId === viewerId);
  const isParty =
    contract.client.userId === viewerId
    || contract.creator.userId === viewerId
    || (contract.collaborators || []).includes(viewerId);

  const cancellable = isParty && (contract.acceptanceState === 'pending_other_side' || contract.acceptanceState === 'draft');
  const disputable = isParty && contract.acceptanceState === 'active';
  // Any party (incl. collaborators) can share the project for collaboration.
  const shareable = isParty;

  if (!(cancellable && isInitiator) && !disputable && !shareable) return null;

  const handleCancel = async () => {
    setBusy('cancel');
    const result = await contractApiService.cancel(contract.id);
    setBusy(null);
    if (!result.success) {
      showToast(translateBackendError(result.error, '操作失败，请稍后重试'), 'error');
      return;
    }
    dispatch({ type: 'REPLACE_BUNDLE', bundle: result.bundle });
    setCancelOpen(false);
  };

  return (
    <>
      <OverflowMenu open={menuOpen} onOpenChange={setMenuOpen} disabled={busy !== null}>
        {shareable && (
          <MenuItem onClick={() => { setMenuOpen(false); setShareOpen(true); }}>
            <Share2 className="w-4 h-4" /> 分享项目
          </MenuItem>
        )}
        {cancellable && isInitiator && (
          <MenuItem danger onClick={() => { setMenuOpen(false); setCancelOpen(true); }}>
            <X className="w-4 h-4" /> 取消合同
          </MenuItem>
        )}
        {disputable && (
          <MenuItem danger onClick={() => { setMenuOpen(false); setDisputeOpen(true); }}>
            <ShieldAlert className="w-4 h-4" /> 发起申诉
          </MenuItem>
        )}
      </OverflowMenu>
      {shareOpen && <ShareProjectModal contract={contract} onClose={() => setShareOpen(false)} />}
      {cancelOpen && (
        <ConfirmActionModal
          title="取消合同"
          message="确认取消该合同？取消后合同将被关闭，对方会收到通知，且无法恢复。如已发起签署流程也将一并终止。"
          confirmLabel="确认取消合同"
          confirmTone="danger"
          busy={busy === 'cancel'}
          onConfirm={handleCancel}
          onClose={() => setCancelOpen(false)}
        />
      )}
      {disputeOpen && (
        <RaiseDisputeModal
          bundle={bundle}
          onClose={() => setDisputeOpen(false)}
          onSubmitted={(b) => dispatch({ type: 'REPLACE_BUNDLE', bundle: b })}
        />
      )}
    </>
  );
};

// Accept / reject for the receiving side, cancel for either side while
// the contract is still pre-active. On a successful response the server
// returns the updated bundle and we replace the local cache entry.
const LifecycleActions: React.FC<{ bundle: ContractBundle }> = ({ bundle }) => {
  const navigate = useNavigate();
  const { dispatch, viewerId, viewerVerified } = useContracts();
  const { showToast } = useToast();
  const [busy, setBusy] = useState<string | null>(null);
  const [acceptOpen, setAcceptOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [verifyPromptOpen, setVerifyPromptOpen] = useState(false);
  // After signing kicks off, remind the user the e签宝 SMS is on its way.
  const [signSmsOpen, setSignSmsOpen] = useState(false);

  const contract = bundle.contract;
  const isInitiator =
    (contract.initiatorRole === 'client' && contract.client.userId === viewerId) ||
    (contract.initiatorRole === 'creator' && contract.creator.userId === viewerId);
  const isReceiver =
    (contract.initiatorRole === 'client' && contract.creator.userId === viewerId) ||
    (contract.initiatorRole === 'creator' && contract.client.userId === viewerId);

  // Cancel + dispute now live in the header overflow menu
  // (ContractOverflowMenu). This row only holds the primary, visible
  // actions: accept / reject / refresh-signing / download-PDF / reinitiate.
  const acceptable = isReceiver && contract.acceptanceState === 'pending_other_side' && !contract.esignFlowId;
  const rejectable = isReceiver && contract.acceptanceState === 'pending_other_side' && !contract.esignFlowId;
  const showRejectionReason = contract.acceptanceState === 'rejected' && Boolean(contract.rejectionReason);
  // The initiator can tweak a rejected contract and send a fresh one.
  const reinitiable = isInitiator && contract.acceptanceState === 'rejected';

  if (!acceptable && !rejectable && !contract.esignFlowId && !showRejectionReason && !reinitiable) return null;

  const onResult = (result: { success: boolean; bundle?: ContractBundle; error?: string }) => {
    setBusy(null);
    if (!result.success || !result.bundle) {
      showToast(translateBackendError(result.error, '操作失败，请稍后重试'), 'error');
      return;
    }
    dispatch({ type: 'REPLACE_BUNDLE', bundle: result.bundle });
  };

  // Clicking 接受并发起签署: real-name verification is required before
  // e签宝 can create the signing flow, so pre-check here and route the
  // user to verification instead of letting the backend reject it.
  const onAcceptClick = () => {
    if (!viewerVerified) {
      setVerifyPromptOpen(true);
      return;
    }
    setAcceptOpen(true);
  };

  const handleAccept = async () => {
    setBusy('accept');
    const result = await contractApiService.accept(contract.id);
    setBusy(null);
    if (!result.success) {
      // Backend is the source of truth — if it still says unverified
      // (e.g. the other party), surface the verify prompt.
      const err = result.error || '';
      if (/NOT_VERIFIED|实名/.test(err)) {
        setAcceptOpen(false);
        setVerifyPromptOpen(true);
        return;
      }
      showToast(translateBackendError(result.error, '操作失败，请稍后重试'), 'error');
      return;
    }
    dispatch({ type: 'REPLACE_BUNDLE', bundle: result.bundle });
    setAcceptOpen(false);
    // Signing flow started — both parties get an e签宝 SMS to sign.
    setSignSmsOpen(true);
  };
  const handleReject = async (reason: string) => {
    setBusy('reject');
    const result = await contractApiService.reject(contract.id, reason);
    onResult(result);
    if (result.success) setRejectOpen(false);
  };
  const handleRefreshSigning = async () => {
    setBusy('refresh');
    const result = await contractApiService.refreshSigning(contract.id);
    setBusy(null);
    if (!result.success) {
      showToast(translateBackendError(result.error, '刷新失败'), 'error');
      return;
    }
    dispatch({ type: 'REPLACE_BUNDLE', bundle: result.bundle });
    if (result.bundle.contract.acceptanceState === 'active') {
      showToast('双方已完成签署，合同已生效', 'success');
    } else {
      showToast('已刷新签署状态', 'info');
    }
  };

  const handleDownloadPdf = async () => {
    setBusy('download');
    const result = await contractApiService.getSignedPdfUrl(contract.id);
    setBusy(null);
    if (!result.success) {
      showToast(translateBackendError(result.error, '获取合同 PDF 失败'), 'error');
      return;
    }
    window.open(result.downloadUrl, '_blank');
  };

  return (
    <div className="mt-5 pt-4 border-t border-slate-100 flex flex-wrap items-center gap-2">
      {contract.acceptanceState === 'rejected' && contract.rejectionReason && (
        <span className="w-full text-xs text-rose-700 bg-rose-50 border border-rose-100 rounded-md px-2.5 py-1.5">
          拒绝理由：{contract.rejectionReason}
        </span>
      )}
      {contract.esignFlowId && contract.acceptanceState === 'pending_other_side' && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-md px-2.5 py-1.5">
            已发起 e签宝 签署流程，等待双方完成签署
            {contract.clientSignStatus === 'signed' && ' · 甲方已签'}
            {contract.creatorSignStatus === 'signed' && ' · 乙方已签'}
          </span>
          <button
            onClick={handleRefreshSigning}
            disabled={busy !== null}
            className="text-xs px-2.5 py-1.5 rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50"
          >
            {busy === 'refresh' ? '刷新中...' : '刷新签署状态'}
          </button>
        </div>
      )}
      {/* e签宝 only serves the document once the flow is archived (both
          parties signed → contract active). Hide download until then. */}
      {contract.esignFlowId && contract.acceptanceState === 'active' && (
        <button
          onClick={handleDownloadPdf}
          disabled={busy !== null}
          className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
        >
          <Download className="w-4 h-4" /> {busy === 'download' ? '获取中...' : '下载合同 PDF'}
        </button>
      )}
      {acceptable && (
        <button
          onClick={onAcceptClick}
          disabled={busy !== null}
          className="px-3.5 py-2 text-sm rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
        >
          接受并发起签署
        </button>
      )}
      {rejectable && (
        <button
          onClick={() => setRejectOpen(true)}
          disabled={busy !== null}
          className="px-3.5 py-2 text-sm rounded-lg bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
        >
          拒绝合同
        </button>
      )}
      {reinitiable && (
        <button
          onClick={() => navigate('/contracts/new', { state: { cloneFrom: bundle } })}
          className="px-3.5 py-2 text-sm rounded-lg bg-slate-900 text-white hover:bg-slate-800"
        >
          修改并重新发起
        </button>
      )}
      {acceptOpen && (
        <ConfirmActionModal
          title="接受合同"
          message="接受后将发起 e签宝 电子签署流程，双方完成签署后合同正式生效。请确认你已阅读并认可合同的里程碑、付款节点与验收标准。"
          confirmLabel="确认接受并签署"
          confirmTone="primary"
          busy={busy === 'accept'}
          onConfirm={handleAccept}
          onClose={() => setAcceptOpen(false)}
        />
      )}
      {rejectOpen && (
        <ConfirmActionModal
          title="拒绝合同"
          message="拒绝后合同将被关闭，发起方会收到通知。请填写拒绝理由，方便对方了解原因并酌情修改后重新发起。"
          confirmLabel="确认拒绝"
          confirmTone="danger"
          reasonLabel="拒绝理由"
          reasonPlaceholder="例如：报价超出预算 / 交付周期过长 / 里程碑划分需调整……"
          reasonRequired
          busy={busy === 'reject'}
          onConfirm={handleReject}
          onClose={() => setRejectOpen(false)}
        />
      )}
      {verifyPromptOpen && (
        <ConfirmActionModal
          title="需要先完成实名认证"
          message="电子签署合同前需完成实名认证（用于 e签宝 合法签约）。现在前往「设置 - 实名认证」完成认证后即可发起签署。"
          confirmLabel="前往实名认证"
          confirmTone="primary"
          onConfirm={() => { setVerifyPromptOpen(false); navigate('/settings?tab=verification'); }}
          onClose={() => setVerifyPromptOpen(false)}
        />
      )}
      {signSmsOpen && (
        <ConfirmActionModal
          title="已发起签署，请查收短信"
          message="e签宝 已向签署双方发送签署短信，请在手机上查收并按提示完成签署。双方签署完成后合同正式生效。你也可以稍后用「刷新签署状态」查看进度。"
          confirmLabel="我知道了"
          confirmTone="primary"
          onConfirm={() => setSignSmsOpen(false)}
          onClose={() => setSignSmsOpen(false)}
        />
      )}
    </div>
  );
};

// Three-dots overflow menu for less-common / destructive contract
// actions. Closes on outside-click or Escape.
const OverflowMenu: React.FC<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  disabled?: boolean;
  children: React.ReactNode;
}> = ({ open, onOpenChange, disabled, children }) => {
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onOpenChange(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onOpenChange(false); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onOpenChange]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => onOpenChange(!open)}
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="更多操作"
        className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-slate-700 disabled:opacity-50"
      >
        <MoreVertical className="w-4 h-4" />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full z-20 mt-1.5 min-w-[10rem] overflow-hidden rounded-lg border border-slate-200 bg-white py-1 shadow-lg"
        >
          {children}
        </div>
      )}
    </div>
  );
};

const MenuItem: React.FC<{
  onClick: () => void;
  danger?: boolean;
  children: React.ReactNode;
}> = ({ onClick, danger, children }) => (
  <button
    type="button"
    role="menuitem"
    onClick={onClick}
    className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm ${
      danger ? 'text-rose-600 hover:bg-rose-50' : 'text-slate-700 hover:bg-slate-50'
    }`}
  >
    {children}
  </button>
);

const RaiseDisputeModal: React.FC<{
  bundle: ContractBundle;
  onClose: () => void;
  onSubmitted: (b: ContractBundle) => void;
}> = ({ bundle, onClose, onSubmitted }) => {
  const { showToast } = useToast();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    const result = await contractApiService.raiseDispute(bundle.contract.id, reason);
    setBusy(false);
    if (!result.success) {
      showToast(translateBackendError(result.error, '发起争议失败'), 'error');
      return;
    }
    onSubmitted(result.bundle);
    onClose();
  };

  return (
    <Modal title="发起申诉" onClose={onClose}>
      <p className="text-sm text-slate-500 mb-4">
        发起申诉后合同将进入争议处理状态，由平台介入协调。请如实描述申诉内容。
      </p>
      <label className="block text-xs text-slate-500 mb-1">申诉原因 <span className="text-rose-500">*</span></label>
      <textarea
        value={reason}
        onChange={e => setReason(e.target.value)}
        rows={5}
        placeholder="请详细描述申诉事项，包括相关里程碑/付款节点、对方的不当行为或理由分歧。"
        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-rose-100"
      />
      <div className="flex justify-end gap-2">
        <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 rounded-lg">取消</button>
        <button
          onClick={submit}
          disabled={!reason.trim() || busy}
          className="px-4 py-2 bg-rose-600 text-white text-sm rounded-lg disabled:opacity-50 hover:bg-rose-700"
        >
          {busy ? '提交中...' : '提交申诉'}
        </button>
      </div>
    </Modal>
  );
};

// =====================================================================
// Completion section — mutual reviews + invoice (发票)
// =====================================================================

const CLIENT_DIMENSIONS = [
  { key: 'outputQuality', label: '作品质量' },
  { key: 'communication', label: '沟通效率' },
  { key: 'timeliness', label: '交付准时' },
  { key: 'professionalism', label: '专业程度' },
];
const CREATOR_DIMENSIONS = [
  { key: 'communication', label: '沟通效率' },
  { key: 'requirementClarity', label: '需求清晰' },
  { key: 'paymentTimeliness', label: '付款及时' },
  { key: 'cooperationAttitude', label: '合作态度' },
];
const CLIENT_GOOD_TAGS = ['出片质量高', '沟通顺畅', '交付准时', '超出预期', '修改耐心', '创意出色'];
const CLIENT_BAD_TAGS = ['交付延迟', '沟通不及时', '与预期不符', '需多次返工'];
const CREATOR_GOOD_TAGS = ['需求明确', '反馈及时', '付款爽快', '尊重创作', '沟通友好'];
const CREATOR_BAD_TAGS = ['需求模糊', '反复修改', '付款拖延', '沟通困难'];

const StarRating: React.FC<{ value: number; onChange?: (v: number) => void; size?: number }> = ({ value, onChange, size = 5 }) => {
  const [hover, setHover] = useState(0);
  const cls = size === 4 ? 'w-4 h-4' : 'w-5 h-5';
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          disabled={!onChange}
          onClick={() => onChange?.(star)}
          onMouseEnter={() => onChange && setHover(star)}
          onMouseLeave={() => onChange && setHover(0)}
          className={onChange ? 'p-0.5 transition-transform hover:scale-110' : 'p-0.5 cursor-default'}
        >
          <Star className={`${cls} ${star <= (hover || value) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} />
        </button>
      ))}
    </div>
  );
};

const CompletionSection: React.FC<{ bundle: ContractBundle }> = ({ bundle }) => {
  const { isViewerClient, isViewerCreator, viewerId } = useContracts();
  const isClient = isViewerClient(bundle);
  const isCreator = isViewerCreator(bundle);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [invoiceOpen, setInvoiceOpen] = useState(false);

  const myReview = bundle.reviews.find(r => r.reviewerUserId === viewerId);
  const theirReview = bundle.reviews.find(r => r.reviewerUserId !== viewerId);
  const otherName = isClient ? bundle.contract.creator.nickname : bundle.contract.client.nickname;

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6 mb-6">
      <div className="flex items-center gap-2 mb-1">
        <CheckCircle2 className="w-5 h-5 text-emerald-600" />
        <h2 className="text-base font-semibold text-slate-900">合作已完成</h2>
      </div>
      <p className="text-sm text-slate-500 mb-5">双方可互相评价；创作者可为本次合作上传发票。</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* Reviews column */}
        <div>
          <h3 className="text-sm font-medium text-slate-700 mb-3">互评</h3>
          <div className="space-y-3">
            {/* My review */}
            {myReview ? (
              <ReviewCard title="我的评价" review={myReview} />
            ) : (isClient || isCreator) ? (
              <button
                onClick={() => setReviewOpen(true)}
                className="w-full rounded-xl border border-dashed border-slate-300 py-4 text-sm text-slate-600 hover:border-blue-300 hover:text-blue-600 inline-flex items-center justify-center gap-1.5"
              >
                <Star className="w-4 h-4" /> 评价{otherName}
              </button>
            ) : null}

            {/* Their review */}
            {theirReview ? (
              <ReviewCard title="对方的评价" review={theirReview} />
            ) : (
              <div className="rounded-xl border border-slate-100 bg-slate-50/60 py-4 text-center text-xs text-slate-400">
                对方还未评价
              </div>
            )}
          </div>
        </div>

        {/* Invoice column */}
        <div>
          <h3 className="text-sm font-medium text-slate-700 mb-3 inline-flex items-center gap-1.5">
            <Receipt className="w-4 h-4" /> 发票
            {bundle.contract.requiresInvoice && (
              <span className="text-[10px] px-1.5 py-0.5 bg-amber-50 text-amber-700 rounded">本合同需开具发票</span>
            )}
          </h3>
          <div className="space-y-2">
            {bundle.invoices.length > 0 ? (
              bundle.invoices.map(inv => <InvoiceCard key={inv.id} invoice={inv} />)
            ) : (
              <div className="rounded-xl border border-slate-100 bg-slate-50/60 py-4 text-center text-xs text-slate-400">
                {isCreator ? '暂无发票' : '暂无发票，等待创作者开具并上传'}
              </div>
            )}
            {isCreator && (
              <button
                onClick={() => setInvoiceOpen(true)}
                className="w-full rounded-xl border border-dashed border-slate-300 py-3 text-sm text-slate-600 hover:border-blue-300 hover:text-blue-600 inline-flex items-center justify-center gap-1.5"
              >
                <UploadIcon className="w-4 h-4" /> 上传发票
              </button>
            )}
          </div>
        </div>
      </div>

      {reviewOpen && (
        <ReviewModal
          bundle={bundle}
          role={isClient ? 'client_to_creator' : 'creator_to_client'}
          revieweeName={otherName}
          onClose={() => setReviewOpen(false)}
        />
      )}
      {invoiceOpen && (
        <InvoiceModal bundle={bundle} onClose={() => setInvoiceOpen(false)} />
      )}
    </div>
  );
};

const ReviewCard: React.FC<{ title: string; review: ContractBundle['reviews'][number] }> = ({ title, review }) => (
  <div className="rounded-xl border border-slate-200 p-3">
    <div className="flex items-center justify-between mb-1">
      <span className="text-xs font-medium text-slate-700">{title}</span>
      <StarRating value={review.rating} size={4} />
    </div>
    {review.dimensionRatings && Object.keys(review.dimensionRatings).length > 0 && (
      <div className="flex flex-wrap gap-x-3 gap-y-1 mb-1.5">
        {Object.entries(review.dimensionRatings).map(([k, v]) => {
          const label = [...CLIENT_DIMENSIONS, ...CREATOR_DIMENSIONS].find(d => d.key === k)?.label || k;
          return <span key={k} className="text-[11px] text-slate-500">{label} <span className="text-yellow-500 font-medium">{v}★</span></span>;
        })}
      </div>
    )}
    {review.comment && <p className="text-xs text-slate-600 leading-relaxed">{review.comment}</p>}
    <div className="text-[10px] text-slate-400 mt-1">{formatDateTime(review.createdAt)}</div>
  </div>
);

const InvoiceCard: React.FC<{ invoice: ContractInvoice }> = ({ invoice }) => {
  const download = async () => {
    try {
      const res = await fetch(invoice.fileUrl);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = invoice.fileName || 'invoice';
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch { window.open(invoice.fileUrl, '_blank', 'noopener'); }
  };
  return (
    <div className="flex items-center gap-2.5 rounded-xl border border-slate-200 p-2.5">
      <div className="flex h-10 w-10 items-center justify-center rounded-md bg-slate-50 text-slate-400 shrink-0">
        <Receipt className="w-5 h-5" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-xs font-medium text-slate-800">{invoice.fileName}</div>
        <div className="text-[10px] text-slate-400">
          {invoice.amount != null ? `${formatRmb(invoice.amount)} · ` : ''}{formatDateTime(invoice.createdAt)}
        </div>
      </div>
      <button onClick={download} className="inline-flex items-center gap-1 text-[11px] text-slate-600 hover:text-slate-900 shrink-0">
        <Download className="w-3 h-3" /> 下载
      </button>
    </div>
  );
};

const ReviewModal: React.FC<{
  bundle: ContractBundle;
  role: 'client_to_creator' | 'creator_to_client';
  revieweeName: string;
  onClose: () => void;
}> = ({ bundle, role, revieweeName, onClose }) => {
  const { dispatch } = useContracts();
  const { showToast } = useToast();
  const dimensions = role === 'client_to_creator' ? CLIENT_DIMENSIONS : CREATOR_DIMENSIONS;
  const goodTags = role === 'client_to_creator' ? CLIENT_GOOD_TAGS : CREATOR_GOOD_TAGS;
  const badTags = role === 'client_to_creator' ? CLIENT_BAD_TAGS : CREATOR_BAD_TAGS;

  const [ratings, setRatings] = useState<Record<string, number>>({});
  const [comment, setComment] = useState('');
  const [selectedTags, setSelectedTags] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);

  const overall = dimensions.length > 0
    ? Math.round((Object.values(ratings).reduce((a, b) => a + b, 0) / dimensions.length) * 10) / 10
    : 0;

  const toggleTag = (tag: string) => {
    setSelectedTags(prev => {
      const next = new Set(prev);
      if (next.has(tag)) {
        next.delete(tag);
        setComment(c => c.replace(tag, '').replace(/，\s*，/g, '，').replace(/^，\s*|，\s*$/g, '').trim());
      } else {
        next.add(tag);
        setComment(c => (c.includes(tag) ? c : c ? `${c}，${tag}` : tag));
      }
      return next;
    });
  };

  const submit = async () => {
    const unrated = dimensions.filter(d => !ratings[d.key]);
    if (unrated.length > 0) { showToast(`请完成所有评分（${unrated.map(d => d.label).join('、')}）`, 'error'); return; }
    if (!comment.trim()) { showToast('请填写评价内容', 'error'); return; }
    setBusy(true);
    const result = await contractApiService.submitReview(bundle.contract.id, {
      rating: Math.round(overall) || 1,
      comment: comment.trim(),
      dimensionRatings: ratings,
    });
    setBusy(false);
    if (!result.success) { showToast(translateBackendError(result.error, '提交评价失败'), 'error'); return; }
    dispatch({ type: 'REPLACE_BUNDLE', bundle: result.bundle });
    showToast('评价已提交', 'success');
    onClose();
  };

  return (
    <Modal title={`评价 ${revieweeName}`} onClose={onClose}>
      <div className="space-y-5">
        <div className="space-y-2.5">
          {dimensions.map(dim => (
            <div key={dim.key} className="flex items-center justify-between">
              <span className="text-sm text-slate-700 font-medium">{dim.label}</span>
              <StarRating value={ratings[dim.key] || 0} onChange={(v) => setRatings(prev => ({ ...prev, [dim.key]: v }))} />
            </div>
          ))}
          {overall > 0 && (
            <div className="flex items-center justify-between pt-2 border-t border-slate-100">
              <span className="text-sm font-semibold text-slate-900">综合评分</span>
              <span className="text-lg font-bold text-yellow-500">{overall}</span>
            </div>
          )}
        </div>

        <div>
          <label className="block text-xs text-slate-500 mb-2">快捷印象（点击加入评价）</label>
          <div className="flex flex-wrap gap-2 mb-2">
            {goodTags.map(tag => (
              <button key={tag} type="button" onClick={() => toggleTag(tag)}
                className={`px-3 py-1 rounded-full text-xs font-medium border transition-all inline-flex items-center gap-1 ${selectedTags.has(tag) ? 'bg-emerald-50 border-emerald-300 text-emerald-700' : 'bg-white border-slate-200 text-slate-600 hover:border-emerald-300'}`}>
                <ThumbsUp className="w-3 h-3" /> {tag}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            {badTags.map(tag => (
              <button key={tag} type="button" onClick={() => toggleTag(tag)}
                className={`px-3 py-1 rounded-full text-xs font-medium border transition-all inline-flex items-center gap-1 ${selectedTags.has(tag) ? 'bg-rose-50 border-rose-300 text-rose-700' : 'bg-white border-slate-200 text-slate-600 hover:border-rose-300'}`}>
                <ThumbsDown className="w-3 h-3" /> {tag}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-xs text-slate-500 mb-2">详细评价</label>
          <textarea value={comment} onChange={e => setComment(e.target.value)} rows={4} maxLength={500}
            placeholder="分享你的合作体验..."
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 resize-none" />
          <div className="text-right text-[11px] text-slate-400 mt-1">{comment.length}/500</div>
        </div>

        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 rounded-lg">取消</button>
          <button onClick={submit} disabled={busy}
            className="px-6 py-2 text-sm font-medium text-white bg-slate-900 rounded-lg hover:bg-slate-800 disabled:opacity-50">
            {busy ? '提交中...' : '提交评价'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

const InvoiceModal: React.FC<{ bundle: ContractBundle; onClose: () => void }> = ({ bundle, onClose }) => {
  const { dispatch } = useContracts();
  const { showToast } = useToast();
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | undefined>();
  const [fileUrl, setFileUrl] = useState<string | undefined>();
  const [uploading, setUploading] = useState(false);
  const [amount, setAmount] = useState<string>(String(bundle.contract.totalAmount || ''));
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const pick = async (f: File) => {
    setFile(f); setUploading(true); setFileUrl(undefined);
    try {
      const url = await mediaUploadService.uploadFile(f, f.name, f.type);
      setFileUrl(url);
    } catch (e: any) {
      showToast(e?.message || '上传失败', 'error');
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    if (!fileUrl) { showToast('请先上传发票文件', 'error'); return; }
    setBusy(true);
    const result = await contractApiService.submitInvoice(bundle.contract.id, {
      fileUrl,
      fileName: file?.name || '发票',
      amount: amount ? Number(amount) : undefined,
      note: note.trim() || undefined,
    });
    setBusy(false);
    if (!result.success) { showToast(translateBackendError(result.error, '上传发票失败'), 'error'); return; }
    dispatch({ type: 'REPLACE_BUNDLE', bundle: result.bundle });
    showToast('发票已上传', 'success');
    onClose();
  };

  return (
    <Modal title="上传发票" onClose={onClose}>
      <p className="text-xs text-slate-500 mb-4">为本次合作上传发票文件（PDF 或图片）。客户将能在合同详情中查看与下载。</p>

      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        className="w-full mb-4 rounded-lg border-2 border-dashed border-slate-200 py-5 text-sm text-slate-500 hover:border-blue-300 hover:text-blue-600 disabled:opacity-50"
      >
        <span className="inline-flex items-center gap-1.5">
          <UploadIcon className="w-4 h-4" />
          {uploading ? '上传中…' : fileUrl ? '重新选择文件' : '选择发票文件'}
        </span>
        {file && <div className="text-[11px] text-slate-400 mt-1 truncate px-4">{file.name}{fileUrl ? ' · 已上传' : ''}</div>}
      </button>
      <input ref={inputRef} type="file" accept="application/pdf,image/*" className="hidden"
        onChange={e => { const f = e.target.files?.[0]; if (f) pick(f); e.target.value = ''; }} />

      <div className="grid grid-cols-2 gap-3 mb-4">
        <div>
          <label className="block text-xs text-slate-500 mb-1">发票金额 (¥)</label>
          <input type="number" value={amount} onChange={e => setAmount(e.target.value)}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-100" />
        </div>
        <div>
          <label className="block text-xs text-slate-500 mb-1">备注（可选）</label>
          <input type="text" value={note} onChange={e => setNote(e.target.value)} placeholder="如发票类型/抬头"
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-100" />
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 rounded-lg">取消</button>
        <button onClick={submit} disabled={busy || uploading || !fileUrl}
          className="px-6 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50">
          {busy ? '提交中...' : '提交发票'}
        </button>
      </div>
    </Modal>
  );
};

const LargeProjectView: React.FC<{ bundle: ContractBundle }> = ({ bundle }) => {
  return (
    <div className="grid grid-cols-3 gap-6">
      <div className="col-span-2 space-y-3">
        <h2 className="text-sm font-medium text-slate-700">里程碑</h2>
        {bundle.milestones.map(m => (
          <MilestoneCard key={m.id} bundle={bundle} milestone={m} />
        ))}
        <ChangesSection bundle={bundle} />
        <DisputesSection bundle={bundle} />
      </div>
      <div className="col-span-1 space-y-6">
        <PaymentPlanPanel bundle={bundle} />
        <ReleasePlanPanel bundle={bundle} />
      </div>
    </div>
  );
};

// =====================================================================
// Small project view — single delivery, simpler layout
// =====================================================================

const SmallProjectView: React.FC<{ bundle: ContractBundle }> = ({ bundle }) => {
  return (
    <div className="grid grid-cols-3 gap-6">
      <div className="col-span-2">
        {bundle.milestones.map(m => (
          <MilestoneCard key={m.id} bundle={bundle} milestone={m} />
        ))}
        <ChangesSection bundle={bundle} />
        <DisputesSection bundle={bundle} />
      </div>
      <div className="col-span-1 space-y-6">
        <PaymentPlanPanel bundle={bundle} />
        <ReleasePlanPanel bundle={bundle} />
      </div>
    </div>
  );
};

// =====================================================================
// Milestone card with creator/client actions
// =====================================================================

const MilestoneCard: React.FC<{ bundle: ContractBundle; milestone: Milestone }> = ({ bundle, milestone }) => {
  const { isViewerClient, isViewerCreator, dispatch } = useContracts();
  const isClient = isViewerClient(bundle);
  const isCreator = isViewerCreator(bundle);
  const covered = isMilestoneAssetCovered(bundle, milestone.id);
  const [revisionOpen, setRevisionOpen] = useState(false);
  const [submitOpen, setSubmitOpen] = useState(false);
  const { showToast } = useToast();
  const [milestoneBusy, setMilestoneBusy] = useState(false);

  const handleAcceptMilestone = async () => {
    setMilestoneBusy(true);
    const result = await contractApiService.acceptMilestone(bundle.contract.id, milestone.id);
    setMilestoneBusy(false);
    if (!result.success) {
      showToast(translateBackendError(result.error, '验收失败，请稍后重试'), 'error');
      return;
    }
    dispatch({ type: 'REPLACE_BUNDLE', bundle: result.bundle });
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1.5">
            <MilestoneStatusPill status={milestone.status} />
            <CoverageBadge covered={covered} />
            <span className="text-xs text-slate-400">截止 {formatDate(milestone.dueAt)}</span>
            {milestone.acceptancePeriodEndsAt && milestone.status === 'IN_ACCEPTANCE' && (
              <span className="text-xs text-amber-600 inline-flex items-center gap-1">
                <Clock className="w-3 h-3" /> 验收期至 {formatDate(milestone.acceptancePeriodEndsAt)}
              </span>
            )}
          </div>
          <h3 className="font-semibold text-slate-900">{milestone.title}</h3>
          <p className="text-sm text-slate-500 mt-1">{milestone.description}</p>
        </div>
        <div className="text-right shrink-0">
          <div className="text-base font-semibold text-slate-900">{formatRmb(milestone.amount)}</div>
          <div className="text-[11px] text-slate-400">本阶段金额</div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 mt-3 pt-3 border-t border-slate-100 text-xs">
        <div>
          <div className="text-slate-400 mb-1">验收标准</div>
          <div className="text-slate-700">{milestone.acceptanceCriteria}</div>
        </div>
        <div>
          <div className="text-slate-400 mb-1">交付物</div>
          <div className="text-slate-700">
            {milestone.requiredAssets.map((a, i) => (
              <span key={i} className="inline-block mr-1.5 mb-1 text-[11px] px-1.5 py-0.5 bg-slate-100 rounded">
                {a.label}
              </span>
            ))}
          </div>
        </div>
      </div>

      {milestone.submittedAssets.length > 0 && (
        <DeliverablesList assets={milestone.submittedAssets} />
      )}

      <RevisionHistory milestone={milestone} />

      <div className="mt-3 pt-3 border-t border-slate-100 text-[11px] text-slate-400 flex items-center gap-3">
        <span>已用修改次数 {milestone.revisionCountUsed}/{milestone.revisionLimit}</span>
        {(milestone.revisionHistory || []).length === 0 && milestone.revisionDueAt && (
          <span>· 返修截止 {formatDate(milestone.revisionDueAt)}</span>
        )}
      </div>

      {/* Action row */}
      <div className="mt-4 pt-4 border-t border-slate-100 flex flex-wrap items-center gap-2">
        {/* Creator actions */}
        {isCreator && (
          <>
            {milestone.status === 'IN_PROGRESS' || milestone.status === 'REVISION_REQUESTED' || milestone.status === 'SUBMITTED' ? (
              covered ? (
                <button
                  onClick={() => setSubmitOpen(true)}
                  className="px-3 py-1.5 bg-emerald-600 text-white rounded-md text-xs font-medium hover:bg-emerald-700 inline-flex items-center gap-1.5"
                >
                  <Play className="w-3 h-3" /> 提交作品
                </button>
              ) : (
                <button
                  disabled
                  title="当前阶段未资金覆盖，可暂停工作"
                  className="px-3 py-1.5 bg-slate-100 text-slate-400 rounded-md text-xs font-medium inline-flex items-center gap-1.5"
                >
                  <Pause className="w-3 h-3" /> 暂停工作（未资金覆盖）
                </button>
              )
            ) : null}
            {milestone.status === 'NOT_STARTED' && covered && (
              <button
                onClick={() => setSubmitOpen(true)}
                className="px-3 py-1.5 bg-emerald-600 text-white rounded-md text-xs font-medium inline-flex items-center gap-1.5"
              >
                <Play className="w-3 h-3" /> 开始并提交
              </button>
            )}
            {/* Already delivered, still within the client's acceptance
                window — let the creator update / re-submit the work. */}
            {milestone.status === 'IN_ACCEPTANCE' && (
              <button
                onClick={() => setSubmitOpen(true)}
                className="px-3 py-1.5 border border-emerald-200 text-emerald-700 rounded-md text-xs font-medium hover:bg-emerald-50 inline-flex items-center gap-1.5"
              >
                <RefreshCw className="w-3 h-3" /> 重新提交 / 修改作品
              </button>
            )}
          </>
        )}

        {/* Client actions */}
        {isClient && milestone.status === 'IN_ACCEPTANCE' && (
          <>
            <button
              onClick={handleAcceptMilestone}
              disabled={milestoneBusy}
              className="px-3 py-1.5 bg-slate-900 text-white rounded-md text-xs font-medium hover:bg-slate-800 inline-flex items-center gap-1.5 disabled:opacity-50"
            >
              <CheckCircle2 className="w-3 h-3" /> {milestoneBusy ? '处理中...' : '确认验收'}
            </button>
            <button
              onClick={() => setRevisionOpen(true)}
              className="px-3 py-1.5 border border-slate-200 text-slate-700 rounded-md text-xs font-medium hover:bg-slate-50 inline-flex items-center gap-1.5"
            >
              <RefreshCw className="w-3 h-3" /> 要求修改 / 变更
            </button>
            <button
              onClick={handleAcceptMilestone}
              disabled={milestoneBusy}
              title="演示用：模拟客户超时未操作触发的自动验收（实际由 cron 触发）"
              className="px-2 py-1 text-[10px] text-slate-400 border border-slate-200 rounded-md hover:bg-slate-50 disabled:opacity-50"
            >
              模拟超时
            </button>
          </>
        )}

        {milestone.status === 'COMPLETED' && (
          <span className="text-xs text-emerald-700 inline-flex items-center gap-1">
            <CheckCircle2 className="w-3 h-3" /> 已验收，已放款
          </span>
        )}
      </div>

      {revisionOpen && (
        <RevisionModal bundle={bundle} milestone={milestone} onClose={() => setRevisionOpen(false)} />
      )}
      {submitOpen && (
        <SubmitDeliveryModal bundle={bundle} milestone={milestone} onClose={() => setSubmitOpen(false)} />
      )}
    </div>
  );
};

// Submitted deliverables, shown to BOTH client and creator. Images get
// an inline thumbnail with a lightbox; every asset with a real fileUrl
// gets 预览 (open in new tab) + 下载 actions. Assets without a file
// (legacy placeholder rows) fall back to a plain checkmark line.
const DeliverablesList: React.FC<{ assets: DeliveryAsset[] }> = ({ assets }) => {
  const [lightbox, setLightbox] = useState<string | null>(null);

  const download = async (url: string, label: string) => {
    // The media bucket is private; the server hands us a short-lived
    // signed GET URL. Fetch it as a blob so we can force a real download
    // with a sensible filename (a cross-origin <a download> is ignored).
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(String(res.status));
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = label || 'download';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objectUrl);
    } catch {
      // Fallback: just open it in a new tab.
      window.open(url, '_blank', 'noopener');
    }
  };

  return (
    <div className="mt-3 pt-3 border-t border-slate-100">
      <div className="text-xs text-slate-400 mb-2 inline-flex items-center gap-1">
        <Paperclip className="w-3 h-3" /> 已提交交付物
        <span className="text-slate-300">· {assets.length} 个</span>
      </div>
      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {assets.map((a, i) => {
          const file = hasFile(a.fileUrl);
          const img = isImageUrl(a.fileUrl);
          const vid = isVideoUrl(a.fileUrl);
          const openable = img || vid;
          return (
            <li
              key={i}
              className="flex items-center gap-2.5 rounded-lg border border-slate-200 bg-slate-50/60 p-2"
            >
              <div className="shrink-0">
                {img ? (
                  <button
                    type="button"
                    onClick={() => setLightbox(a.fileUrl!)}
                    className="block h-11 w-11 overflow-hidden rounded-md border border-slate-200 bg-white"
                    title="点击放大预览"
                  >
                    <img src={a.fileUrl} alt={a.label} className="h-full w-full object-cover" />
                  </button>
                ) : vid ? (
                  <button
                    type="button"
                    onClick={() => setLightbox(a.fileUrl!)}
                    className="relative block h-11 w-11 overflow-hidden rounded-md border border-slate-200 bg-black"
                    title="点击播放"
                  >
                    <video src={a.fileUrl} className="h-full w-full object-cover" muted preload="metadata" />
                    <span className="absolute inset-0 flex items-center justify-center">
                      <Play className="w-4 h-4 text-white drop-shadow" />
                    </span>
                  </button>
                ) : (
                  <div className="flex h-11 w-11 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-400">
                    {file ? <FileText className="w-5 h-5" /> : <CheckCircle2 className="w-5 h-5 text-emerald-500" />}
                  </div>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-xs font-medium text-slate-800">{a.label}</div>
                <div className="text-[10px] text-slate-400">
                  {ASSET_TYPE_LABELS[a.type] || a.type}
                  {a.uploadedAt ? ` · ${formatDateTime(a.uploadedAt)}` : ''}
                </div>
                {file && (
                  <div className="mt-1 flex items-center gap-2.5">
                    <button
                      type="button"
                      onClick={() => download(a.fileUrl!, a.label)}
                      className="inline-flex items-center gap-1 text-[11px] text-slate-600 hover:text-slate-900"
                    >
                      <Download className="w-3 h-3" /> 下载
                    </button>
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ul>
      {lightbox && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center bg-black/80 p-6"
          onClick={() => setLightbox(null)}
        >
          <button
            className="absolute top-5 right-5 flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20"
            onClick={() => setLightbox(null)}
            aria-label="关闭"
          >
            <X className="w-5 h-5" />
          </button>
          {isVideoUrl(lightbox) ? (
            <video
              src={lightbox}
              controls
              autoPlay
              className="max-h-[90vh] max-w-[90vw] rounded-lg shadow-2xl"
              onClick={e => e.stopPropagation()}
            />
          ) : (
            <img
              src={lightbox}
              alt="预览"
              className="max-h-[90vh] max-w-[90vw] rounded-lg object-contain shadow-2xl"
              onClick={e => e.stopPropagation()}
            />
          )}
        </div>
      )}
    </div>
  );
};

const RevisionHistory: React.FC<{ milestone: Milestone }> = ({ milestone }) => {
  const history = milestone.revisionHistory || [];
  const [expanded, setExpanded] = useState(false);
  if (history.length === 0) return null;

  // Newest first
  const ordered = [...history].sort((a, b) => b.requestedAt - a.requestedAt);
  const latest = ordered[0];
  const visible = expanded ? ordered : [latest];

  return (
    <div className="mt-3 pt-3 border-t border-slate-100">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] font-medium text-orange-800 inline-flex items-center gap-1">
          <RefreshCw className="w-3 h-3" /> 客户的修改要求
          <span className="ml-1 text-orange-600/70 font-normal">· {history.length} 条</span>
        </div>
        {ordered.length > 1 && (
          <button
            type="button"
            onClick={() => setExpanded(v => !v)}
            className="text-[11px] text-orange-700 hover:text-orange-900"
          >
            {expanded ? `折叠（仅显示最新）` : `查看全部 ${ordered.length} 条`}
          </button>
        )}
      </div>
      <ol className="space-y-2">
        {visible.map((r, idx) => {
          // index in original chronological order so user sees "第 N 次"
          const seqNo = ordered.length - ordered.indexOf(r);
          return (
            <li key={r.id} className="rounded-md bg-orange-50 border border-orange-100 p-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[11px] font-medium text-orange-900 inline-flex items-center gap-1.5">
                  第 {seqNo} 次修改要求
                  <RevisionCategoryBadge category={r.category} />
                  <span className="text-orange-600/80 font-normal">{formatDateTime(r.requestedAt)}</span>
                </span>
                <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${
                  r.resolvedAt
                    ? 'bg-emerald-100 text-emerald-700'
                    : 'bg-orange-100 text-orange-700'
                }`}>
                  {r.resolvedAt ? `已返修 · ${formatDate(r.resolvedAt)}` : '待返修'}
                </span>
              </div>
              <div className="text-xs text-orange-900 whitespace-pre-wrap leading-relaxed">{r.reason}</div>
              <div className="text-[11px] text-orange-700 mt-1.5">
                {r.category === 'invalid_delivery'
                  ? <>原截止日：<span className="font-medium">{formatDate(r.returnDueAt)}</span>（不延长）</>
                  : <>返修截止：{formatDate(r.returnDueAt)}</>
                }
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
};

const RevisionCategoryBadge: React.FC<{ category?: 'detail' | 'invalid_delivery' | 'change' }> = ({ category }) => {
  if (!category || category === 'change') return null;
  const map = {
    detail: { text: '细节优化', cls: 'bg-blue-100 text-blue-800' },
    invalid_delivery: { text: '无效交付', cls: 'bg-rose-100 text-rose-800' },
  };
  const m = map[category];
  if (!m) return null;
  return <span className={`text-[9.5px] font-medium px-1.5 py-0.5 rounded ${m.cls}`}>{m.text}</span>;
};

const MilestoneStatusPill: React.FC<{ status: MilestoneStatus }> = ({ status }) => {
  const map: Record<MilestoneStatus, { text: string; cls: string }> = {
    NOT_STARTED:        { text: '未开始',     cls: 'bg-slate-100 text-slate-600' },
    IN_PROGRESS:        { text: '进行中',     cls: 'bg-blue-100 text-blue-700' },
    SUBMITTED:          { text: '待校验',     cls: 'bg-amber-100 text-amber-700' },
    IN_ACCEPTANCE:      { text: '验收中',     cls: 'bg-amber-100 text-amber-700' },
    REVISION_REQUESTED: { text: '修改中',     cls: 'bg-orange-100 text-orange-700' },
    COMPLETED:          { text: '已完成',     cls: 'bg-emerald-100 text-emerald-700' },
    CANCELLED:          { text: '已取消',     cls: 'bg-slate-100 text-slate-500' },
  };
  const { text, cls } = map[status];
  return <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${cls}`}>{text}</span>;
};

const CoverageBadge: React.FC<{ covered: boolean }> = ({ covered }) => (
  <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded inline-flex items-center gap-1 ${
    covered ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'
  }`}>
    {covered ? <CheckCircle2 className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
    {covered ? '已资金覆盖' : '未资金覆盖'}
  </span>
);

const StateBadge: React.FC<{ state: string }> = ({ state }) => {
  const map: Record<string, { text: string; cls: string }> = {
    draft:               { text: '草稿',           cls: 'bg-slate-100 text-slate-600' },
    pending_other_side:  { text: '等待对方确认',   cls: 'bg-amber-100 text-amber-700' },
    rejected:            { text: '已拒绝',         cls: 'bg-rose-100 text-rose-700' },
    active:              { text: '进行中',         cls: 'bg-emerald-100 text-emerald-700' },
    completed:           { text: '已完成',         cls: 'bg-blue-100 text-blue-700' },
    cancelled:           { text: '已取消',         cls: 'bg-slate-100 text-slate-500' },
    disputed:            { text: '争议中',         cls: 'bg-rose-100 text-rose-700' },
  };
  const { text, cls } = map[state] || map.draft;
  return <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${cls}`}>{text}</span>;
};

const PartyChip: React.FC<{ role: string; nickname: string; highlighted?: boolean }> = ({ role, nickname, highlighted }) => (
  <div className={`inline-flex items-center gap-1.5 ${highlighted ? 'text-slate-900 font-medium' : 'text-slate-600'}`}>
    <span className="text-xs text-slate-400">{role}：</span>
    <span className={`px-2 py-0.5 rounded ${highlighted ? 'bg-blue-50 text-blue-700' : 'bg-slate-100'}`}>
      {nickname}
    </span>
    {highlighted && <span className="text-[10px] text-blue-600">（你）</span>}
  </div>
);

const Metric: React.FC<{ label: string; value: string; positive?: boolean; hint?: string }> = ({ label, value, positive, hint }) => (
  <div>
    <div className={`text-base font-semibold ${positive ? 'text-emerald-600' : 'text-slate-900'}`}>{value}</div>
    <div className="text-[11px] text-slate-400 mt-0.5">
      {label}{hint && <span className="ml-1 text-slate-500">· {hint}</span>}
    </div>
  </div>
);

// =====================================================================
// Plan panels
// =====================================================================

const PaymentPlanPanel: React.FC<{ bundle: ContractBundle }> = ({ bundle }) => {
  const { isViewerClient, dispatch } = useContracts();
  const isClient = isViewerClient(bundle);
  const { showToast } = useToast();
  const [paymentBusy, setPaymentBusy] = useState<string | null>(null);

  const onResult = (
    result: { success: boolean; bundle?: ContractBundle; error?: string },
    failureMsg: string,
  ) => {
    setPaymentBusy(null);
    if (!result.success || !result.bundle) {
      showToast(translateBackendError(result.error, failureMsg), 'error');
      return;
    }
    dispatch({ type: 'REPLACE_BUNDLE', bundle: result.bundle });
  };

  const handleMarkPaid = async (paymentId: string) => {
    setPaymentBusy(`paid-${paymentId}`);
    onResult(await contractApiService.markPaymentPaid(bundle.contract.id, paymentId), '标记付款失败');
  };
  const handleConfirm = async (paymentId: string) => {
    setPaymentBusy(`confirm-${paymentId}`);
    onResult(await contractApiService.confirmPayment(bundle.contract.id, paymentId), '平台确认失败');
  };
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-slate-700">付款计划（客户 → 平台）</h3>
        {bundle.contract.requiresInvoice && (
          <span className="text-[10px] px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded">需发票（合同级）</span>
        )}
      </div>
      <ul className="space-y-3 text-xs">
        {bundle.paymentPlan.map(p => (
          <li key={p.id} className="border-l-2 pl-3 py-1 border-slate-200">
            <div className="flex items-center justify-between mb-1">
              <span className="font-medium text-slate-900">第 {p.sequenceIndex + 1} 笔 · {formatRmb(p.amount)}</span>
              <PaymentStatusPill status={p.status} />
            </div>
            <div className="text-slate-500 leading-relaxed">
              {paymentTriggerLabel(p, bundle)}
              {p.isFinalTail && <span className="ml-1 text-amber-600">· 尾款</span>}
            </div>
            <div className="text-[10px] text-slate-400 mt-1 flex flex-wrap gap-1">
              {p.platformConfirmedAt && <span>平台确认 {formatDate(p.platformConfirmedAt)}</span>}
            </div>
            {(p.coversMilestoneIds.length > 0 || p.coversTail) && (
              <div className="text-[10px] text-slate-400 mt-1">
                覆盖：
                {p.coversMilestoneIds
                  .map(id => bundle.milestones.find(m => m.id === id)?.title)
                  .filter(Boolean)
                  .join(' + ')}
                {p.coversTail && (
                  <span className={p.coversMilestoneIds.length > 0 ? 'ml-1 text-amber-700' : 'text-amber-700'}>
                    {p.coversMilestoneIds.length > 0 ? '+ ' : ''}尾款
                  </span>
                )}
              </div>
            )}
            {isClient && p.status === 'DUE' && (
              <button
                onClick={() => handleMarkPaid(p.id)}
                disabled={paymentBusy !== null}
                className="mt-2 text-[11px] px-2 py-1 bg-slate-900 text-white rounded hover:bg-slate-800 disabled:opacity-50"
              >
                {paymentBusy === `paid-${p.id}` ? '处理中...' : '我已付款'}
              </button>
            )}
            {p.status === 'PAID_BY_CLIENT' && (
              <button
                onClick={() => handleConfirm(p.id)}
                disabled={paymentBusy !== null}
                className="mt-2 text-[10px] px-2 py-1 border border-slate-200 text-slate-500 rounded hover:bg-slate-50 disabled:opacity-50"
                title="演示用：模拟平台收款webhook（生产环境由 汇付天下 webhook 触发）"
              >
                {paymentBusy === `confirm-${p.id}` ? '处理中...' : '模拟平台收款'}
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

const ReleasePlanPanel: React.FC<{ bundle: ContractBundle }> = ({ bundle }) => {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4">
      <h3 className="text-sm font-medium text-slate-700 mb-3">放款计划（平台 → 创作者）</h3>
      <ul className="space-y-2 text-xs">
        {bundle.releasePlan.map(r => {
          const m = bundle.milestones.find(mm => mm.id === r.milestoneId);
          return (
            <li key={r.id} className="border-l-2 pl-3 py-1 border-slate-200">
              <div className="flex items-center justify-between mb-0.5">
                <span className="font-medium text-slate-900">{m?.title || `第 ${r.sequenceIndex + 1} 次`}</span>
                <ReleaseStatusPill status={r.status} />
              </div>
              <div className="text-slate-500">{formatRmb(r.amount)}</div>
              {r.releasedAt && <div className="text-[10px] text-slate-400 mt-0.5">放款于 {formatDate(r.releasedAt)}</div>}
              {r.lateFeeApplied != null && r.lateFeeApplied > 0 && (
                <div className="text-[10px] text-rose-600 mt-0.5">
                  扣除逾期赔付 {formatRmb(r.lateFeeApplied)}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

const PaymentStatusPill: React.FC<{ status: PaymentEntryStatus }> = ({ status }) => {
  const map: Record<PaymentEntryStatus, { text: string; cls: string }> = {
    PENDING:             { text: '待触发', cls: 'bg-slate-100 text-slate-600' },
    DUE:                 { text: '待付款', cls: 'bg-amber-100 text-amber-700' },
    PAID_BY_CLIENT:      { text: '客户已付', cls: 'bg-blue-100 text-blue-700' },
    PLATFORM_CONFIRMED:  { text: '平台已收', cls: 'bg-emerald-100 text-emerald-700' },
    OVERDUE:             { text: '逾期', cls: 'bg-rose-100 text-rose-700' },
  };
  const { text, cls } = map[status];
  return <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${cls}`}>{text}</span>;
};

const ReleaseStatusPill: React.FC<{ status: ReleasePlanEntry['status'] }> = ({ status }) => {
  const map: Record<ReleasePlanEntry['status'], { text: string; cls: string }> = {
    PENDING:               { text: '未放款',     cls: 'bg-slate-100 text-slate-600' },
    BLOCKED_NO_FUNDING:    { text: '资金不足',   cls: 'bg-rose-100 text-rose-700' },
    RELEASED:              { text: '已放款',     cls: 'bg-emerald-100 text-emerald-700' },
  };
  const { text, cls } = map[status];
  return <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${cls}`}>{text}</span>;
};

function paymentTriggerLabel(p: PaymentPlanEntry, bundle: ContractBundle): string {
  switch (p.trigger) {
    case 'CONTRACT_SIGNED': return '合同签署后';
    case 'MILESTONE_PASSED': {
      const m = bundle.milestones.find(mm => mm.id === p.triggerMilestoneId);
      return `${m?.title || '指定里程碑'} 验收后`;
    }
    case 'FINAL_TAIL': return '最终验收后';
  }
}

// =====================================================================
// Modals
// =====================================================================

type RevisionReason = 'detail' | 'change' | 'invalid_delivery';

const RevisionModal: React.FC<{
  bundle: ContractBundle; milestone: Milestone;
  onClose: () => void;
}> = ({ bundle, milestone, onClose }) => {
  const { dispatch } = useContracts();
  const { showToast } = useToast();
  const limit = milestone.revisionLimit;
  const used = milestone.revisionCountUsed;
  const limitReached = used >= limit;

  // Default to 细节优化 unless the limit is reached, in which case
  // the buyer must pick 变更单 or 无效交付 (neither counts).
  const [reason, setReason] = useState<RevisionReason>(limitReached ? 'change' : 'detail');
  const [reasonText, setReasonText] = useState('');
  const [returnDate, setReturnDate] = useState(new Date(Date.now() + 3 * 24 * 3600 * 1000).toISOString().slice(0, 10));

  // Change-specific fields (used when reason === 'change')
  const [changeScope, setChangeScope] = useState('');
  const [changeFeeDelta, setChangeFeeDelta] = useState(0);
  const [changeDays, setChangeDays] = useState(0);

  const [busy, setBusy] = useState(false);
  const detailDisabled = limitReached;

  const submitDetail = async () => {
    setBusy(true);
    const result = await contractApiService.requestRevision(bundle.contract.id, milestone.id, {
      category: 'detail',
      reason: reasonText,
      revisionDueAt: new Date(returnDate).getTime(),
    });
    setBusy(false);
    if (!result.success) {
      showToast(translateBackendError(result.error, '提交修改请求失败'), 'error');
      return;
    }
    dispatch({ type: 'REPLACE_BUNDLE', bundle: result.bundle });
    onClose();
  };

  const submitInvalid = async () => {
    setBusy(true);
    const result = await contractApiService.requestRevision(bundle.contract.id, milestone.id, {
      category: 'invalid_delivery',
      reason: reasonText,
      revisionDueAt: milestone.dueAt,
    });
    setBusy(false);
    if (!result.success) {
      showToast(translateBackendError(result.error, '提交失败'), 'error');
      return;
    }
    dispatch({ type: 'REPLACE_BUNDLE', bundle: result.bundle });
    onClose();
  };

  const submitChange = async () => {
    setBusy(true);
    const result = await contractApiService.proposeChange(bundle.contract.id, {
      scopeDelta: changeScope,
      feeDelta: changeFeeDelta,
      additionalDays: changeDays,
    });
    setBusy(false);
    if (!result.success) {
      showToast(translateBackendError(result.error, '提交变更单失败'), 'error');
      return;
    }
    dispatch({ type: 'REPLACE_BUNDLE', bundle: result.bundle });
    onClose();
  };

  return (
    <Modal title="客户请求修改 / 变更" onClose={onClose}>
      <p className="text-sm text-slate-500 mb-4">
        请选择本次请求的理由。理由会决定截止日是否延长、是否计入修改次数、以及是否需要额外付款。
      </p>
      <div className="grid grid-cols-3 gap-2.5 mb-5">
        <ChoiceCard
          title="① 细节优化"
          desc={`原需求范围内调整。已用 ${used}/${limit} 次。延长交付时间，免费。`}
          accentColor="blue"
          selected={reason === 'detail'}
          disabled={detailDisabled}
          onClick={() => setReason('detail')}
        />
        <ChoiceCard
          title="② 需求变动 / 新增需求"
          desc="超出原需求。需重新议价并生成变更单：延长交付时间 + 额外付款。"
          accentColor="amber"
          selected={reason === 'change'}
          onClick={() => setReason('change')}
        />
        <ChoiceCard
          title="③ 无效交付"
          desc="本次交付不符合验收标准。不延长截止日；逾期由乙方承担。不计修改次数。"
          accentColor="rose"
          selected={reason === 'invalid_delivery'}
          onClick={() => setReason('invalid_delivery')}
        />
      </div>

      {reason === 'detail' && !limitReached && (
        <>
          <label className="block text-xs text-slate-500 mb-1">具体优化点</label>
          <textarea value={reasonText} onChange={e => setReasonText(e.target.value)} rows={3}
            placeholder="请描述需要细节优化的地方..."
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-blue-100" />
          <label className="block text-xs text-slate-500 mb-1">双方确认的返修截止时间（延长当前阶段截止日）</label>
          <input type="date" value={returnDate} onChange={e => setReturnDate(e.target.value)}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm mb-5 focus:outline-none focus:ring-2 focus:ring-blue-100" />
          <div className="flex justify-end gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 rounded-lg">取消</button>
            <button
              disabled={!reasonText.trim() || busy}
              onClick={submitDetail}
              className="px-4 py-2 bg-slate-900 text-white text-sm rounded-lg disabled:opacity-50 hover:bg-slate-800"
            >
              {busy ? '提交中...' : '提交细节优化请求'}
            </button>
          </div>
        </>
      )}
      {reason === 'detail' && limitReached && (
        <div className="text-sm text-amber-700 bg-amber-50 border border-amber-100 rounded-lg p-3">
          已达本里程碑细节优化次数上限（{used}/{limit}），请改为发起"需求变更"或"无效交付"。
        </div>
      )}

      {reason === 'change' && (
        <>
          <div className="text-sm text-amber-800 bg-amber-50 border border-amber-100 rounded-lg p-3 mb-4">
            需求变动 / 新增需求会生成变更单：延长交付时间 + 客户额外付款。变更单需创作者同意后生效；费用会折算入下一笔付款，时间会顺延当前阶段截止日。
          </div>
          <label className="block text-xs text-slate-500 mb-1">变更内容 <span className="text-rose-500">*</span></label>
          <textarea value={changeScope} onChange={e => setChangeScope(e.target.value)} rows={3}
            placeholder="例：增加 1 个 30 秒预告片版本"
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-amber-100" />
          <div className="grid grid-cols-2 gap-3 mb-5">
            <div>
              <label className="block text-xs text-slate-500 mb-1">追加费用 (¥)</label>
              <input type="number" value={changeFeeDelta} onChange={e => setChangeFeeDelta(Number(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-100" />
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">追加天数</label>
              <input type="number" value={changeDays} onChange={e => setChangeDays(Number(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-100" />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 rounded-lg">取消</button>
            <button
              disabled={!changeScope.trim() || busy}
              onClick={submitChange}
              className="px-4 py-2 bg-amber-600 text-white text-sm rounded-lg disabled:opacity-50 hover:bg-amber-700"
            >
              {busy ? '提交中...' : '提交变更单'}
            </button>
          </div>
        </>
      )}

      {reason === 'invalid_delivery' && (
        <>
          <div className="text-sm text-rose-800 bg-rose-50 border border-rose-100 rounded-lg p-3 mb-4 leading-relaxed">
            <strong>不延长当前阶段截止日。</strong>
            该里程碑原截止日：<span className="font-medium">{formatDate(milestone.dueAt)}</span>。
            如创作者无法在此之前完成有效交付，将按原合同记为逾期，由乙方承担相应责任。本次请求不计入细节优化次数。
          </div>
          <label className="block text-xs text-slate-500 mb-1">不符合验收标准的具体说明 <span className="text-rose-500">*</span></label>
          <textarea value={reasonText} onChange={e => setReasonText(e.target.value)} rows={4}
            placeholder="请对照验收标准，说明本次交付为何不符合（例如：缺少源文件、画质不达要求、未提供商用授权书等）..."
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-rose-100" />
          <div className="flex justify-end gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 rounded-lg">取消</button>
            <button
              disabled={!reasonText.trim() || busy}
              onClick={submitInvalid}
              className="px-4 py-2 bg-rose-600 text-white text-sm rounded-lg disabled:opacity-50 hover:bg-rose-700"
            >
              {busy ? '提交中...' : '提交无效交付反馈'}
            </button>
          </div>
        </>
      )}
    </Modal>
  );
};

const ChoiceCard: React.FC<{
  title: string; desc: string;
  accentColor: 'blue' | 'amber' | 'rose';
  selected: boolean; disabled?: boolean;
  onClick?: () => void;
}> = ({ title, desc, accentColor, selected, disabled, onClick }) => {
  const palette = {
    blue:  { bg: 'bg-blue-50',  border: 'border-blue-300',  text: 'text-blue-900',  ring: 'ring-blue-200' },
    amber: { bg: 'bg-amber-50', border: 'border-amber-300', text: 'text-amber-900', ring: 'ring-amber-200' },
    rose:  { bg: 'bg-rose-50',  border: 'border-rose-300',  text: 'text-rose-900',  ring: 'ring-rose-200' },
  }[accentColor];
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`text-left p-3 rounded-lg border transition-colors flex flex-col items-start justify-start ${
        disabled
          ? 'border-slate-200 bg-slate-50 opacity-50 cursor-not-allowed'
          : selected
            ? `${palette.border} ${palette.bg} ring-2 ring-offset-1 ${palette.ring}`
            : 'border-slate-200 hover:bg-slate-50'
      }`}
    >
      <div className={`text-sm font-medium ${selected && !disabled ? palette.text : 'text-slate-900'}`}>{title}</div>
      <div className="text-xs text-slate-500 mt-1 leading-relaxed">{desc}</div>
    </button>
  );
};

// Max number of files a creator can attach to a single submission.
const MAX_DELIVERY_FILES = 5;

// A pending upload in the submit modal. The creator freely chooses how
// many files to attach (up to MAX_DELIVERY_FILES); each tracks its own
// upload state.
interface UploadSlot {
  type: DeliveryAsset['type'];
  label: string;
  file?: File;
  fileUrl?: string;       // set once uploaded
  uploading: boolean;
  error?: string;
}

// Guess a delivery-asset type from the file's mime/extension so the
// stored asset carries a sensible category.
function assetTypeForFile(file: File): DeliveryAsset['type'] {
  const name = file.name.toLowerCase();
  if (file.type.startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp|svg)$/.test(name)) return 'no_watermark';
  if (/\.(psd|ai|sketch|fig|xcf)$/.test(name)) return 'source_file';
  if (/\.(blend|c4d|fbx|obj|glb|gltf|max)$/.test(name)) return 'model_file';
  if (/\.(aep|prproj|drp|zip|rar|7z)$/.test(name)) return 'engineering_file';
  return 'other';
}

const SubmitDeliveryModal: React.FC<{ bundle: ContractBundle; milestone: Milestone; onClose: () => void }> = ({ bundle, milestone, onClose }) => {
  const { dispatch } = useContracts();
  const { showToast } = useToast();
  const [busy, setBusy] = useState(false);
  // Pre-seed with any already-submitted assets so a re-submit can keep,
  // rename, or remove them rather than starting over.
  const [slots, setSlots] = useState<UploadSlot[]>(() =>
    (milestone.submittedAssets || [])
      .filter(a => a.fileUrl && a.fileUrl !== '#')
      // Strip the signed query string — persist only the base OSS URL;
      // the backend re-signs on read.
      .slice(0, MAX_DELIVERY_FILES)
      .map(a => ({ type: a.type, label: a.label, fileUrl: (a.fileUrl || '').split('?')[0], uploading: false } as UploadSlot))
  );
  const inputRef = React.useRef<HTMLInputElement>(null);

  const patchSlot = (idx: number, patch: Partial<UploadSlot>) =>
    setSlots(prev => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)));

  const uploadAt = async (idx: number, file: File) => {
    try {
      const url = await mediaUploadService.uploadFile(file, file.name, file.type);
      patchSlot(idx, { fileUrl: url, uploading: false });
    } catch (e: any) {
      patchSlot(idx, { uploading: false, error: e?.message || '上传失败' });
    }
  };

  // Add picked files (respecting the 5-file cap), then kick off uploads.
  const addFiles = (files: FileList | File[]) => {
    const incoming = Array.from(files);
    setSlots(prev => {
      const room = MAX_DELIVERY_FILES - prev.length;
      if (room <= 0) {
        showToast(`最多上传 ${MAX_DELIVERY_FILES} 个文件`, 'info');
        return prev;
      }
      if (incoming.length > room) {
        showToast(`最多上传 ${MAX_DELIVERY_FILES} 个文件，仅添加前 ${room} 个`, 'info');
      }
      const picked = incoming.slice(0, room);
      const startIdx = prev.length;
      const next = [
        ...prev,
        ...picked.map(f => ({
          type: assetTypeForFile(f), label: f.name, file: f, uploading: true,
        } as UploadSlot)),
      ];
      // Fire uploads after state commits.
      picked.forEach((f, i) => { void uploadAt(startIdx + i, f); });
      return next;
    });
  };

  const removeSlot = (idx: number) => setSlots(prev => prev.filter((_, i) => i !== idx));
  const retry = (idx: number) => {
    const s = slots[idx];
    if (!s?.file) return;
    patchSlot(idx, { uploading: true, error: undefined });
    void uploadAt(idx, s.file);
  };

  const anyUploading = slots.some(s => s.uploading);
  const uploaded = slots.filter(s => hasFile(s.fileUrl));
  const atCapacity = slots.length >= MAX_DELIVERY_FILES;
  const canSubmit = !busy && !anyUploading && uploaded.length > 0;

  const handleSubmit = async () => {
    setBusy(true);
    const assets = uploaded.map(s => ({
      type: s.type, label: s.label, fileUrl: s.fileUrl!, uploadedAt: Date.now(),
    }));
    const result = await contractApiService.submitDelivery(bundle.contract.id, milestone.id, {
      assets,
      // Platform doesn't auto-judge — buyer decides during 验收期.
      validityPassed: true,
    });
    setBusy(false);
    if (!result.success) {
      showToast(translateBackendError(result.error, '提交失败，请稍后重试'), 'error');
      return;
    }
    dispatch({ type: 'REPLACE_BUNDLE', bundle: result.bundle });
    showToast('作品已提交，等待客户验收', 'success');
    onClose();
  };

  return (
    <Modal title="提交作品" onClose={onClose}>
      <p className="text-xs text-slate-500 mb-3">
        上传本里程碑的作品文件（最多 {MAX_DELIVERY_FILES} 个）。平台不对"是否为有效交付"做自动判定，仅展示双方约定的验收标准。客户在验收期内确认验收、要求修改或发起争议。
      </p>
      {milestone.acceptanceCriteria && (
        <div className="rounded-md bg-blue-50 border border-blue-100 p-3 mb-3">
          <div className="text-[11px] font-medium text-blue-900 mb-1">验收标准（双方约定）</div>
          <div className="text-xs text-blue-900 whitespace-pre-wrap leading-relaxed">{milestone.acceptanceCriteria}</div>
        </div>
      )}
      {milestone.requiredAssets.length > 0 && (
        <div className="text-[11px] text-slate-500 mb-3">
          约定交付物：
          {milestone.requiredAssets.map((a, i) => (
            <span key={i} className="inline-block ml-1 mb-1 px-1.5 py-0.5 bg-slate-100 rounded text-[10px] text-slate-600">
              {a.label}
            </span>
          ))}
        </div>
      )}

      {slots.length > 0 && (
        <div className="space-y-2.5 mb-3 max-h-[38vh] overflow-y-auto pr-1">
          {slots.map((s, i) => (
            <UploadRow
              key={i}
              slot={s}
              onRemove={() => removeSlot(i)}
              onRetry={s.error ? () => retry(i) : undefined}
              onRename={(label) => patchSlot(i, { label })}
            />
          ))}
        </div>
      )}

      {/* Drop / pick zone */}
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={atCapacity}
        className={`w-full mb-4 rounded-lg border-2 border-dashed py-4 text-sm transition-colors ${
          atCapacity
            ? 'border-slate-100 text-slate-300 cursor-not-allowed'
            : 'border-slate-200 text-slate-500 hover:border-blue-300 hover:text-blue-600'
        }`}
      >
        <span className="inline-flex items-center gap-1.5">
          <Upload className="w-4 h-4" />
          {atCapacity ? `已达上限（${MAX_DELIVERY_FILES} 个）` : '选择文件上传'}
        </span>
        <div className="text-[10px] text-slate-400 mt-1">
          已添加 {slots.length}/{MAX_DELIVERY_FILES}，可一次选择多个文件
        </div>
      </button>
      <input
        ref={inputRef}
        type="file"
        multiple
        className="hidden"
        onChange={e => {
          if (e.target.files?.length) addFiles(e.target.files);
          e.target.value = '';
        }}
      />

      <div className="flex justify-end gap-2">
        <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 rounded-lg">取消</button>
        <button
          onClick={handleSubmit}
          disabled={!canSubmit}
          className="px-4 py-2 bg-emerald-600 text-white text-sm rounded-lg hover:bg-emerald-700 disabled:opacity-50"
          title={uploaded.length === 0 ? '请至少上传一个文件' : undefined}
        >
          {busy ? '提交中...' : anyUploading ? '上传中...' : `提交作品${uploaded.length ? `（${uploaded.length}）` : ''}`}
        </button>
      </div>
    </Modal>
  );
};

// One uploaded file row: thumbnail (for images) + name + state + remove.
// Image files preview as a small thumbnail.
const UploadRow: React.FC<{
  slot: UploadSlot;
  onRemove: () => void;
  onRetry?: () => void;
  onRename?: (label: string) => void;
}> = ({ slot, onRemove, onRetry, onRename }) => {
  const done = hasFile(slot.fileUrl);
  // Prefer the local file for the thumbnail while/after uploading so we
  // don't depend on the (private) remote URL being directly viewable.
  const localPreview = React.useMemo(
    () => (slot.file && slot.file.type.startsWith('image/') ? URL.createObjectURL(slot.file) : undefined),
    [slot.file],
  );
  React.useEffect(() => () => { if (localPreview) URL.revokeObjectURL(localPreview); }, [localPreview]);

  return (
    <div className="flex items-center gap-3 rounded-lg border border-slate-200 p-2.5">
      <div className="shrink-0">
        {localPreview ? (
          <img src={localPreview} alt={slot.label} className="h-12 w-12 rounded-md object-cover border border-slate-200" />
        ) : (
          <div className={`flex h-12 w-12 items-center justify-center rounded-md border ${
            done ? 'border-emerald-200 bg-emerald-50 text-emerald-600'
              : slot.error ? 'border-rose-200 bg-rose-50 text-rose-500'
              : 'border-slate-200 bg-slate-50 text-slate-400'
          }`}>
            {done ? <CheckCircle2 className="w-5 h-5" /> : slot.error ? <AlertCircle className="w-5 h-5" /> : <FileText className="w-5 h-5" />}
          </div>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          {onRename ? (
            <input
              value={slot.label}
              onChange={e => onRename(e.target.value)}
              placeholder="文件名称"
              className="min-w-0 flex-1 text-xs font-medium text-slate-800 border border-transparent hover:border-slate-200 focus:border-blue-300 rounded px-1 py-0.5 bg-transparent focus:bg-white focus:outline-none"
            />
          ) : (
            <span className="truncate text-xs font-medium text-slate-800">{slot.label}</span>
          )}
          <span className="text-[10px] text-slate-400 shrink-0">· {ASSET_TYPE_LABELS[slot.type] || slot.type}</span>
        </div>
        <div className="text-[11px] mt-0.5">
          {slot.uploading ? (
            <span className="text-amber-600">上传中…</span>
          ) : slot.error ? (
            <span className="text-rose-600">{slot.error}</span>
          ) : done ? (
            <span className="text-emerald-600">已上传</span>
          ) : (
            <span className="text-slate-400">等待上传</span>
          )}
        </div>
      </div>
      <div className="shrink-0 flex items-center gap-1.5">
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-[11px] rounded-md border border-slate-200 text-slate-700 hover:bg-slate-50"
          >
            <RefreshCw className="w-3 h-3" /> 重试
          </button>
        )}
        <button
          type="button"
          onClick={onRemove}
          disabled={slot.uploading}
          className="p-1.5 rounded-md text-slate-400 hover:text-rose-600 hover:bg-rose-50 disabled:opacity-40"
          aria-label="移除"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};

// Rendered through a portal to document.body so `position: fixed`
// centers against the viewport — not against a transformed ancestor
// (e.g. a card with hover:-translate-y-1), which would otherwise make
// the modal drift as the card animates.
const Modal: React.FC<{ title: string; onClose: () => void; children: React.ReactNode }> = ({ title, onClose, children }) => (
  createPortal(
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-100"><X className="w-5 h-5 text-slate-500" /></button>
        </div>
        {children}
      </div>
    </div>,
    document.body,
  )
);

// Reusable confirm modal for contract lifecycle actions (accept /
// reject). When `reasonRequired` (or `reasonLabel`) is set, shows a
// textarea and passes its value to onConfirm; otherwise it's a plain
// confirm dialog. The confirm button is disabled until a required
// reason is filled.
const ConfirmActionModal: React.FC<{
  title: string;
  message: string;
  confirmLabel: string;
  confirmTone?: 'primary' | 'danger';
  reasonLabel?: string;
  reasonPlaceholder?: string;
  reasonRequired?: boolean;
  busy?: boolean;
  onConfirm: (reason: string) => void;
  onClose: () => void;
}> = ({
  title, message, confirmLabel, confirmTone = 'primary',
  reasonLabel, reasonPlaceholder, reasonRequired, busy, onConfirm, onClose,
}) => {
  const [reason, setReason] = useState('');
  const showReason = Boolean(reasonLabel) || Boolean(reasonRequired);
  const blocked = busy || (reasonRequired ? !reason.trim() : false);
  const confirmCls = confirmTone === 'danger'
    ? 'bg-rose-600 hover:bg-rose-700'
    : 'bg-emerald-600 hover:bg-emerald-700';

  return (
    <Modal title={title} onClose={onClose}>
      <p className="text-sm text-slate-600 leading-relaxed mb-4">{message}</p>
      {showReason && (
        <>
          <label className="block text-xs text-slate-500 mb-1">
            {reasonLabel || '说明'}{reasonRequired && <span className="text-rose-500"> *</span>}
          </label>
          <textarea
            value={reason}
            onChange={e => setReason(e.target.value)}
            rows={4}
            placeholder={reasonPlaceholder}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-blue-100"
          />
        </>
      )}
      <div className="flex justify-end gap-2">
        <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 rounded-lg">取消</button>
        <button
          onClick={() => onConfirm(reason.trim())}
          disabled={blocked}
          className={`px-4 py-2 text-white text-sm rounded-lg disabled:opacity-50 ${confirmCls}`}
        >
          {busy ? '处理中...' : confirmLabel}
        </button>
      </div>
    </Modal>
  );
};

// =====================================================================
// Changes & Disputes
// =====================================================================

const ChangesSection: React.FC<{ bundle: ContractBundle }> = ({ bundle }) => {
  const { isViewerCreator, isViewerClient, dispatch } = useContracts();
  const { showToast } = useToast();
  const [busy, setBusy] = useState<string | null>(null);
  if (bundle.changes.length === 0) return null;

  const handle = async (changeId: string, kind: 'agree' | 'reject') => {
    setBusy(`${kind}-${changeId}`);
    const result = kind === 'agree'
      ? await contractApiService.agreeChange(bundle.contract.id, changeId)
      : await contractApiService.rejectChange(bundle.contract.id, changeId);
    setBusy(null);
    if (!result.success) {
      showToast(translateBackendError(result.error, '操作失败'), 'error');
      return;
    }
    dispatch({ type: 'REPLACE_BUNDLE', bundle: result.bundle });
  };
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 mt-4">
      <h2 className="text-sm font-medium text-slate-700 mb-3">变更单</h2>
      <ul className="space-y-3">
        {bundle.changes.map(c => (
          <li key={c.id} className="border-l-2 border-amber-200 pl-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-medium text-slate-900">
                由 {c.proposedBy === 'client' ? '客户' : '创作者'} 发起
              </span>
              <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${
                c.status === 'PENDING_AGREE' ? 'bg-amber-100 text-amber-700'
                  : c.status === 'AGREED' ? 'bg-emerald-100 text-emerald-700'
                  : 'bg-rose-100 text-rose-700'
              }`}>
                {c.status === 'PENDING_AGREE' ? '待对方同意' : c.status === 'AGREED' ? '已同意' : '已拒绝'}
              </span>
            </div>
            <div className="text-sm text-slate-700">{c.scopeDelta}</div>
            <div className="text-xs text-slate-500 mt-1">追加 {formatRmb(c.feeDelta)} · 顺延 {c.additionalDays} 天</div>
            {/* Counter-party — the side that didn't propose — can agree
                or reject. */}
            {((c.proposedBy === 'client' && isViewerCreator(bundle)) ||
              (c.proposedBy === 'creator' && isViewerClient(bundle))) &&
              c.status === 'PENDING_AGREE' && (
              <div className="mt-2 flex gap-2">
                <button onClick={() => handle(c.id, 'agree')} disabled={busy !== null}
                  className="px-3 py-1 bg-emerald-600 text-white rounded text-xs disabled:opacity-50">
                  {busy === `agree-${c.id}` ? '处理中...' : '同意'}
                </button>
                <button onClick={() => handle(c.id, 'reject')} disabled={busy !== null}
                  className="px-3 py-1 border border-slate-200 text-slate-600 rounded text-xs disabled:opacity-50">
                  {busy === `reject-${c.id}` ? '处理中...' : '拒绝'}
                </button>
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

const DisputesSection: React.FC<{ bundle: ContractBundle }> = ({ bundle }) => {
  if (bundle.disputes.length === 0) return null;
  return (
    <div className="bg-white rounded-2xl border border-rose-200 bg-rose-50/50 p-5 mt-4">
      <h2 className="text-sm font-medium text-rose-700 mb-3 inline-flex items-center gap-2">
        <ShieldAlert className="w-4 h-4" /> 争议处理
      </h2>
      <ul className="space-y-2 text-sm">
        {bundle.disputes.map(d => (
          <li key={d.id}>
            <span className="font-medium">{d.raisedBy === 'client' ? '客户' : '创作者'}</span> 发起争议 ·
            <span className="text-slate-700 ml-1">{d.reason}</span>
            <div className="text-xs text-slate-500 mt-0.5">{formatDateTime(d.raisedAt)}</div>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default ContractDetailPage;
